import { Button, Link, Modal, ModalContent } from '@heroui/react';
import { Icon } from '@iconify/react';
import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { useGetTrackerInstancesQuery } from '@/patient-app-common/api/trackerApi';
import { useGetSurveyQuery } from '@/patient-app-common/api/surveyApi';
import { copyAndformatVisualQuestions } from '@/patient-app-common/helpers/progress_helper';
import { LineChartModal } from '@/components/progress/charts/line/LineChartModal';
import { MultiLineChartModal } from '@/components/progress/charts/multiline/MultiLineChartModal';
import useResults from '@/patient-app-common/hooks/useResults';
import { ResultCard } from '@/components/survey/result/ResultCard';

import { MetricEntryList } from './MetricEntryList';

export function MetricChart() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  const { trackerId } = useParams();

  const { data: surveyEntries, isLoading: isLoadingEntries } =
    useGetTrackerInstancesQuery(trackerId, {
      skip: !trackerId,
    });

  const { data: survey, isLoading: isLoadingSurvey } = useGetSurveyQuery(
    trackerId,
    {
      refetchOnMountOrArgChange: 3600,
      skip: !trackerId,
    }
  );

  const isLoading = isLoadingEntries || isLoadingSurvey;

  const [modal, toggleModal] = useState(false);

  const chartQuestions = useMemo(() => {
    if (!survey || !surveyEntries?.length) return [];
    return copyAndformatVisualQuestions([survey], surveyEntries);
  }, [survey, surveyEntries]);

  const { createResults } = useResults();
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (hasRedirected.current) return;
    if (!survey) return;

    if (!surveyEntries?.length && !isLoadingEntries) {
      hasRedirected.current = true;
      return navigate(`/tracker/${trackerId}`, { replace: true });
    }
  }, [survey, surveyEntries]);

  useEffect(() => {
    if (!surveyEntries || !survey) return;

    const latestEntry = _.last(_.sortBy(surveyEntries, ['entryDatetime']));
    if (!latestEntry) return;

    const parseRecommendations = async () => {
      const results = await createResults(survey, latestEntry);
      setResult(results[0]);
    };

    parseRecommendations();
  }, [survey, surveyEntries]);

  const addEntry = () => {
    return navigate(`/tracker/${trackerId}`);
  };

  const question = _.first(chartQuestions);

  if (isLoading || _.isEmpty(question)) return null;

  return (
    <>
      <Modal isOpen={modal} onClose={() => toggleModal(false)} size="2xl">
        <ModalContent>
          <MetricEntryList
            question={question}
            survey={survey}
            surveyEntries={surveyEntries}
          />
        </ModalContent>
      </Modal>
      <div className="flex flex-col justify-center items-center p-12">
        <div className="flex flex-col">
          <div className="flex justify-between">
            <Link href="/progress?tab=metrics">
              <Icon icon="mdi:chevron-left" />
              {t('common.action.back')}
            </Link>
            <Button className="ml-auto" onPress={addEntry} color="primary">
              <Icon icon="mdi:plus" />
              {t('common.link.add_entry')}
            </Button>
          </div>
          {!_.isEmpty(question) && <ChartType question={question} />}
        </div>
        {!_.isEmpty(result) && (
          <div className="my-8 w-full md:w-[600px]">
            <ResultCard recommendation={result} />
          </div>
        )}
        <div className="flex flex-col mt-4 space-y-4">
          <Button
            color="primary"
            onPress={() => toggleModal(true)}
            variant="flat"
          >
            {t('common.link.all_entries')}
            <Icon icon="mdi:chevron-right" />
          </Button>
        </div>
      </div>
    </>
  );
}

function ChartType({ question }) {
  switch (question.type) {
    case 'numerical_input':
      return <LineChartModal question={question} initMode="week" />;

    case 'multiple_numerical_input':
      return <MultiLineChartModal question={question} initMode="week" />;

    default:
      return null;
  }
}
